import React from "react";

import { Label } from "../../../../../../components/label";
import { DateTimePicker12H } from "../../../../../../components/datetime-picker-12h";
import { DatePickerMMDDYYYY } from "../../../../../../components/date-picker-mmddyyyy";
import { TimePicker12H } from "../../../../../../components/time-picker-12h";
import { Input } from "../../../../../../components/input";
import { FormErrorText } from "../../../../../../components/form/FormErrorText";
import type { QuestionTypeRendererProps } from "./shared";

type DateTimeQuestionType = "date" | "time" | "datetime";

type DateTimeQuestionProps<TType extends DateTimeQuestionType> = QuestionTypeRendererProps<TType> & {
  kind: TType;
};

export function DateTimeQuestion<TType extends DateTimeQuestionType>({
  question,
  isPreview,
  dateRangeErrorMessage,
  onUpdateQuestion,
  kind,
}: DateTimeQuestionProps<TType>) {
  const { data } = question;
  const updateQuestionData = (updates: Partial<typeof data>) => {
    onUpdateQuestion({
      ...question,
      data: {
        ...data,
        ...updates,
      },
    });
  };

  if (isPreview) {
    if (kind === "date") return <Input type="date" disabled />;
    if (kind === "time") return <Input type="time" disabled />;
    return <Input type="datetime-local" disabled />;
  }

  if (kind === "date") {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Min date</Label>
          <DatePickerMMDDYYYY
            value={data.min_date || ""}
            onChange={(value) => updateQuestionData({ min_date: value })}
            className={`mt-1 ${dateRangeErrorMessage?.minDate ? "border-red-500 dark:border-red-500" : ""}`}
          />
          <FormErrorText message={dateRangeErrorMessage?.minDate} />
        </div>
        <div>
          <Label>Max date</Label>
          <DatePickerMMDDYYYY
            value={data.max_date || ""}
            onChange={(value) => updateQuestionData({ max_date: value })}
            className={`mt-1 ${dateRangeErrorMessage?.maxDate ? "border-red-500 dark:border-red-500" : ""}`}
          />
          <FormErrorText message={dateRangeErrorMessage?.maxDate} />
        </div>
      </div>
    );
  }

  if (kind === "time") {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Min time</Label>
          <TimePicker12H
            value={data.min_time || ""}
            onChange={(value) => updateQuestionData({ min_time: value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label>Max time</Label>
          <TimePicker12H
            value={data.max_time || ""}
            onChange={(value) => updateQuestionData({ max_time: value })}
            className="mt-1"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Earliest allowed date and time</Label>
        <DateTimePicker12H
          value={data.min_datetime || ""}
          onChange={(value) => updateQuestionData({ min_datetime: value })}
          className={`mt-1 ${dateRangeErrorMessage?.minDateTime ? "border-red-500 dark:border-red-500" : ""}`}
        />
        <FormErrorText message={dateRangeErrorMessage?.minDateTime} />
      </div>
      <div>
        <Label>Latest allowed date and time</Label>
        <DateTimePicker12H
          value={data.max_datetime || ""}
          onChange={(value) => updateQuestionData({ max_datetime: value })}
          className={`mt-1 ${dateRangeErrorMessage?.maxDateTime ? "border-red-500 dark:border-red-500" : ""}`}
        />
        <FormErrorText message={dateRangeErrorMessage?.maxDateTime} />
      </div>
    </div>
  );
}

export default DateTimeQuestion;
